import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import useSettingsStore from '../../store/useSettingsStore';
import { cn } from '../../lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuGroup, DropdownMenuShortcut
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Search, Bell, Moon,
  User, LogOut, Settings, ChevronDown,
  PanelLeft, LifeBuoy, Send,
  Maximize, Minimize, LayoutGrid, Languages, Sparkle
} from 'lucide-react';
import { Icon } from '../ui/icon';
import { Badge } from '../ui/badge';
import { notifications, appsList, languagesList } from '@/pages/data/headerData';
const NotificationDot = ({ color }) => (
  <span className={cn(
    "inline-block w-2 h-2 rounded-full shrink-0 mt-1.5",
    color
  )} />
);

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { layoutType, headerType, theme, setTheme, toggleSidebar, primaryColor } = useSettingsStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { t, i18n } = useTranslation();
  const unread = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable full-screen mode:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  if (headerType === 'hidden') return null;

  const isSticky = headerType === 'sticky';

  return (
    <header
      className={cn(
        "h-16 flex items-center px-4 lg:px-6 gap-4 z-30 transition-all duration-200 shrink-0",
        isSticky && "sticky top-0",
        "bg-card/95 backdrop-blur-md border-b border-border shadow-sm"
      )}
    >
      {/* ── Left: Toggle ───────────────────────────── */}
      <div className={cn("flex items-center gap-3", layoutType === 'horizontal' && "lg:hidden")}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
              >
                <Icon icon={PanelLeft} size="sm" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Toggle sidebar</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* ── Center: Search ─────────────────────────── */}
      <div className={cn(
        "flex-1 max-w-xl transition-all duration-300",
        searchOpen ? "max-w-2xl" : "max-w-sm md:max-w-md"
      )}>
        <div className="relative hidden md:block">
          <Icon icon={Search} size="sm" color="muted" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setSearchOpen(false)}
            placeholder={t("Search pages, components, users…")}
            className="pl-9 pr-10 h-9 bg-muted border-transparent hover:border-border focus:border-border focus:bg-background transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:flex items-center gap-1 text-[10px] text-muted-foreground font-mono bg-background border border-border rounded px-1.5 py-0.5">
            <span>⌘</span><span>K</span>
          </kbd>
        </div>
        {/* Mobile search icon */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden h-8 w-8 text-muted-foreground"
        >
          <Icon icon={Search} size="sm" />
        </Button>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 ml-auto">
        {/* ── Theme Toggle ─────────────────────────── */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'galaxy' : 'light';
                  setTheme(nextTheme);
                }}
              >
                {theme === 'light' ? (
                  <Icon icon={Moon} size="sm" color="muted" />
                ) : theme === 'dark' ? (
                  <Icon icon={Sparkle} size="sm" color="primary" />
                ) : (
                  <Icon icon={Moon} size="sm" color="white" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {theme === 'light' ? t('Switch to Dark Mode') : theme === 'dark' ? t('Switch to Galaxy Mode') : t('Switch to Light Mode')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* ── Fullscreen Toggle ────────────────────── */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? (
                  <Icon icon={Minimize} size="sm" color="muted" />
                ) : (
                  <Icon icon={Maximize} size="sm" color="muted" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isFullscreen ? t('Exit full screen') : t('Full screen')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* ── Language Dropdown ───────────────────── */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
            >
              <Icon icon={Languages} size="sm" color="muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 p-1 animate-in fade-in-50 slide-in-from-top-1 duration-200" sideOffset={8}>
            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
              {t("Select Language")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {languagesList.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-muted/80 rounded-md"
                  onClick={() => i18n.changeLanguage(lang.code)}
                >
                  <span className="text-base select-none leading-none">{lang.flag}</span>
                  <span className={cn(
                    "text-sm flex-1",
                    i18n.language === lang.code ? "font-medium text-primary" : "text-foreground"
                  )}>
                    {t(lang.name)}
                  </span>
                  {i18n.language === lang.code && (
                    <Badge className="p-1 rounded-full" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* ── Web Apps Dropdown ───────────────────── */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
            >
              <Icon icon={LayoutGrid} size="sm" color="muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-4 animate-in fade-in-50 slide-in-from-top-1 duration-200" sideOffset={8}>
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-border">
              <h4 className="font-semibold text-sm">{t("Web Apps")}</h4>
              <Badge variant="soft-primary">{t("Launcher")}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {appsList.map((app) => (
                <Link
                  key={app.path}
                  to={app.path}
                  className="flex flex-col items-center justify-center p-3 rounded-xl hover:bg-muted/85 transition-all duration-200 group text-center"
                >
                  <Button
                    variant={`soft-${app.color}`}
                    size="icon"
                    className="mb-2 transition-transform duration-200 group-hover:scale-105"
                  >
                    <Icon icon={app.icon} color={app.color} />
                  </Button>
                  <span className="text-xs font-medium text-foreground truncate w-full">
                    {t(app.label)}
                  </span>
                </Link>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* ── Notifications ─────────────────────────── */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative "
            >
              <Icon icon={Bell} size="sm" color="muted" />
              {unread > 0 && (
                <Badge
                  className="absolute top-1.5 right-1.5 p-0.5 rounded-full animate-pulse-ring"
                />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96 p-0" sideOffset={8}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <h4 className="font-semibold text-sm">{t("Notifications")}</h4>
                <p className="text-xs text-muted-foreground">{unread} {t("unread messages")}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-primary">
                {t("Mark all read")}
              </Button>
            </div>
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {notifications.map(n => (
                <div
                  key={n.id}
                  className={cn(
                    "flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer",
                    !n.read && "bg-primary/3"
                  )}
                >
                  <NotificationDot
                    color={
                      n.type === 'user' ? 'bg-info'
                        : n.type === 'order' ? 'bg-success'
                          : n.type === 'alert' ? 'bg-danger'
                            : 'bg-warning'
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm leading-none mb-0.5", !n.read && "font-semibold")}>{n.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{n.desc}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{n.time}</p>
                  </div>
                  {!n.read && (
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 border-t border-border">
              <Button variant="ghost" size="sm" className="w-full justify-center text-primary">
                {t("View all notifications")}
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* ── User Menu ─────────────────────────────── */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2 h-9 hover:bg-muted/80 rounded-lg"
            >
              <div className="relative">
                <Avatar size="sm" className="ring-2 ring-border">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback
                    className="text-white text-xs font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
                <Badge variant="success" className="absolute -bottom-0.5 -right-0.5 p-1 rounded-full ring-2 ring-background" />
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-semibold leading-none">{user?.name || 'Admin'}</p>
                <p className="text-[10px] text-muted-foreground leading-none mt-0.5">{t("Super Admin")}</p>
              </div>
              <Icon icon={ChevronDown} size="xs" color="muted" className="hidden sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
            <div className="px-2 py-1.5 mb-1">
              <p className="text-sm font-semibold">{user?.name || 'Admin User'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email || 'admin@example.com'}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <Icon icon={User} size="sm" className="mr-2" />
                {t("Profile")}
                <DropdownMenuShortcut>⌘P</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Icon icon={Settings} size="sm" className="mr-2" />
                {t("Settings")}
                <DropdownMenuShortcut>⌘,</DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Icon icon={LifeBuoy} size="sm" className="mr-2" />
                {t("Support")}
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Icon icon={Send} size="sm" className="mr-2" />
                {t("Feedback")}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-danger focus:text-danger focus:bg-danger/10"
              onClick={logout}
            >
              <Icon icon={LogOut} size="sm" color="danger" className="mr-2" />
              {t("Log out")}
              <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;